import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Tabs, Icon, Badge, Button, EmptyState } from '../../components/ui';

const TEMPLES = [
  {
    id: 'ram-mandir',
    name: 'Ram Janmabhoomi Mandir',
    nameHi: 'राम जन्मभूमि मंदिर',
    location: 'Ram Janmabhoomi Path',
    category: 'Main Temple',
    entryFee: 'Free',
    dresscode: 'Modest attire required',
    photography: false,
    timings: {
      summer: '7:00 AM – 11:30 AM, 2:00 PM – 7:00 PM',
      winter: '7:00 AM – 11:30 AM, 2:00 PM – 6:30 PM',
    },
    aarti: [
      { name: 'Mangal Aarti', time: '4:00 AM' },
      { name: 'Shringar Aarti', time: '6:00 AM' },
      { name: 'Bhog Aarti', time: '12:00 PM' },
      { name: 'Shayan Aarti', time: '10:00 PM' },
    ],
    specialNote: 'Separate queues for darshan passes and general entry. Book in advance on official portal.',
    featured: true,
  },
  {
    id: 'hanuman-garhi',
    name: 'Hanuman Garhi',
    nameHi: 'हनुमान गढ़ी',
    location: 'Hanuman Garhi Marg',
    category: 'Fort Temple',
    entryFee: 'Free',
    dresscode: 'No specific requirement',
    photography: true,
    timings: {
      summer: '5:00 AM – 10:00 PM',
      winter: '5:00 AM – 10:00 PM',
    },
    aarti: [
      { name: 'Morning Aarti', time: '5:00 AM' },
      { name: 'Evening Aarti', time: '7:00 PM' },
    ],
    specialNote: '76 steps lead up to the hilltop temple. Shoes must be removed at the base.',
    featured: true,
  },
  {
    id: 'kanak-bhawan',
    name: 'Kanak Bhawan',
    nameHi: 'कनक भवन',
    location: 'Near Hanuman Garhi',
    category: 'Palace Temple',
    entryFee: 'Free',
    dresscode: 'Traditional attire preferred',
    photography: true,
    timings: {
      summer: '8:00 AM – 12:00 PM, 4:00 PM – 9:00 PM',
      winter: '8:00 AM – 12:00 PM, 3:30 PM – 8:30 PM',
    },
    aarti: [
      { name: 'Morning Aarti', time: '8:00 AM' },
      { name: 'Evening Aarti', time: '6:30 PM' },
    ],
    specialNote: 'Famous for the golden shringaar of Ram-Sita idols on special occasions.',
    featured: false,
  },
  {
    id: 'nageshwarnath',
    name: 'Nageshwarnath Temple',
    nameHi: 'नागेश्वरनाथ मंदिर',
    location: 'Naya Ghat Road',
    category: 'Shiva Temple',
    entryFee: 'Free',
    dresscode: 'Modest attire',
    photography: true,
    timings: {
      summer: '5:30 AM – 12:00 PM, 3:00 PM – 9:00 PM',
      winter: '6:00 AM – 12:00 PM, 2:30 PM – 8:30 PM',
    },
    aarti: [
      { name: 'Morning Aarti', time: '5:30 AM' },
      { name: 'Evening Aarti', time: '7:30 PM' },
    ],
    specialNote: 'The only Shiva temple established by Lord Ram himself. Special darshan on Mahashivratri.',
    featured: false,
  },
  {
    id: 'treta-ke-thakur',
    name: 'Treta Ke Thakur',
    nameHi: 'त्रेता के ठाकुर',
    location: 'Naya Ghat',
    category: 'Ancient Temple',
    entryFee: 'Free',
    dresscode: 'Modest attire',
    photography: true,
    timings: {
      summer: '6:00 AM – 12:00 PM, 3:00 PM – 8:00 PM',
      winter: '6:30 AM – 12:00 PM, 2:30 PM – 7:30 PM',
    },
    aarti: [
      { name: 'Morning Aarti', time: '6:00 AM' },
      { name: 'Evening Aarti', time: '6:30 PM' },
    ],
    specialNote: 'Houses idols carved from black stone originally installed by Vikramaditya. Site of Ram\'s Ashwamedha Yajna.',
    featured: false,
  },
  {
    id: 'sita-ki-rasoi',
    name: 'Sita Ki Rasoi',
    nameHi: 'सीता की रसोई',
    location: 'Near Ram Janmabhoomi',
    category: 'Sacred Kitchen',
    entryFee: 'Free',
    dresscode: 'Modest attire',
    photography: true,
    timings: {
      summer: '7:00 AM – 12:00 PM, 2:00 PM – 7:00 PM',
      winter: '7:30 AM – 12:00 PM, 1:30 PM – 6:30 PM',
    },
    aarti: [],
    specialNote: 'Believed to be the royal kitchen of Sita Mata. Houses ancient cooking vessels and utensils.',
    featured: false,
  },
  {
    id: 'dashrath-mahal',
    name: 'Dashrath Mahal',
    nameHi: 'दशरथ महल',
    location: 'Near Ram Janmabhoomi',
    category: 'Palace Temple',
    entryFee: 'Free',
    dresscode: 'Modest attire',
    photography: true,
    timings: {
      summer: '6:00 AM – 12:00 PM, 3:00 PM – 8:00 PM',
      winter: '6:30 AM – 12:00 PM, 2:30 PM – 7:30 PM',
    },
    aarti: [
      { name: 'Morning Aarti', time: '6:00 AM' },
      { name: 'Evening Aarti', time: '6:30 PM' },
    ],
    specialNote: 'The palace of King Dashrath, father of Lord Ram. Beautifully painted murals depict the Ramayana.',
    featured: false,
  },
  {
    id: 'mani-parbat',
    name: 'Mani Parbat',
    nameHi: 'मणि पर्वत',
    location: 'Mani Parbat Road',
    category: 'Hill Shrine',
    entryFee: 'Free',
    dresscode: 'Comfortable footwear',
    photography: true,
    timings: {
      summer: '6:00 AM – 6:00 PM',
      winter: '6:30 AM – 5:30 PM',
    },
    aarti: [],
    specialNote: 'Ancient mound said to have dropped from Hanuman\'s hands while carrying the Sanjeevani herb. Panoramic city views.',
    featured: false,
  },
];

const SEASONS = [
  { id: 'summer', label: 'Summer (Apr – Sep)', icon: 'sun' },
  { id: 'winter', label: 'Winter (Oct – Mar)', icon: 'moon' },
];

export default function TempleTimings() {
  const { t, i18n } = useTranslation();
  const [season, setSeason] = useState('summer');
  const [q, setQ] = useState('');

  const needle = q.trim().toLowerCase();
  const results = needle
    ? TEMPLES.filter((x) => `${x.name} ${x.nameHi} ${x.location} ${x.category}`.toLowerCase().includes(needle))
    : TEMPLES;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Temple Timings & Aarti Schedule in Ayodhya',
    url: 'https://ayodhyadham.info/temple-timings',
    numberOfItems: TEMPLES.length,
    itemListElement: TEMPLES.map((x, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'TouristAttraction',
        name: x.name,
        alternateName: x.nameHi,
        openingHours: x.timings.summer,
        isAccessibleForFree: x.entryFee === 'Free',
        address: { '@type': 'PostalAddress', streetAddress: x.location, addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', addressCountry: 'IN' },
      },
    })),
  };

  return (
    <>
      <SEO
        title="Temple Timings & Aarti Schedule"
        description="Opening hours, aarti timings, entry fees and visitor guidelines for Ram Mandir, Hanuman Garhi, Kanak Bhawan and other temples in Ayodhya."
        canonicalUrl="/temple-timings"
        keywords="Ram Mandir timings, Ayodhya temple timings, aarti schedule Ayodhya, Hanuman Garhi timings"
        structuredData={schema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Temple Timings') }]} title={t('Temple Timings & Aarti Schedule')} subtitle={t('Opening hours, aarti timings and visitor guidelines')} />

      <div className="container listing-wrap">
        <div className="listing__toolbar">
          <Tabs variant="pill" items={SEASONS.map((s) => ({ ...s, label: t(s.label) }))} value={season} onChange={setSeason} ariaLabel={t('Season')} />
          <div className="listing__tools">
            <label className="search">
              <Icon name="search" size={16} />
              <span className="sr-only">{t('Search temples')}</span>
              <input type="search" placeholder={t('Search temples…')} value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
          </div>
        </div>

        <div className="results-meta"><span>{t('{{count}} temples', { count: results.length })}</span></div>

        {results.length === 0 ? (
          <EmptyState icon="temple" title={t('No temples found')} body={t('Try a different search term.')} action={<Button variant="outline" onClick={() => setQ('')}>{t('Clear search')}</Button>} />
        ) : (
          <div className="card-grid card-grid--wide">
            {results.map((x) => (
              <article key={x.id} className="card info-card timing-card">
                <div className="info-card__head">
                  <div>
                    <h2 className="info-card__title">{t(x.name)}</h2>
                    {i18n.language === 'en' && <div className="timing-card__hindi hindi">{x.nameHi}</div>}
                  </div>
                  {x.featured && <Badge variant="gold">{t('Featured')}</Badge>}
                </div>

                <div className="info-card__tags">
                  <Badge variant="soft">{t(x.category)}</Badge>
                  <Badge variant="outline"><Icon name="indian-rupee" size={13} />{t(x.entryFee)}</Badge>
                  <Badge variant="outline"><Icon name="camera" size={13} />{x.photography ? t('Photography allowed') : t('No photography')}</Badge>
                </div>

                <ul className="info-list">
                  <li>
                    <span className="info-list__icon"><Icon name="map-pin" size={14} /></span>
                    <div><div className="info-list__label">{t('Location')}</div><div className="info-list__value">{x.location}</div></div>
                  </li>
                  <li>
                    <span className="info-list__icon"><Icon name="clock" size={14} /></span>
                    <div><div className="info-list__label">{season === 'summer' ? t('Summer timings') : t('Winter timings')}</div><div className="info-list__value">{x.timings[season]}</div></div>
                  </li>
                  <li>
                    <span className="info-list__icon"><Icon name="info" size={14} /></span>
                    <div><div className="info-list__label">{t('Dress code')}</div><div className="info-list__value">{t(x.dresscode)}</div></div>
                  </li>
                </ul>

                {x.aarti.length > 0 && (
                  <div className="timing-card__aarti">
                    <div className="timing-card__aarti-title">{t('Aarti schedule')}</div>
                    {x.aarti.map((a) => (
                      <div key={a.name} className="summary__row">
                        <span>{t(a.name)}</span>
                        <span>{a.time}</span>
                      </div>
                    ))}
                  </div>
                )}

                {x.specialNote && (
                  <div className="notice timing-card__note">
                    <Icon name="info" size={16} />
                    <span>{x.specialNote}</span>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        <div className="cta-band" style={{ marginTop: 36 }}>
          <h2 className="cta-band__title">{t('Ready to plan your darshan?')}</h2>
          <p className="cta-band__sub">{t('Pick a temple and a slot, and we will show you exactly what to do next.')}</p>
          <div className="cta-band__actions">
            <Button to="/darshan-booking" icon={<Icon name="temple" size={16} />} iconPosition="left">{t('Darshan Booking')}</Button>
            <Button variant="outline" to="/travel-guide" icon={<Icon name="map" size={16} />} iconPosition="left">{t('Travel Guide')}</Button>
          </div>
        </div>
      </div>
    </>
  );
}
